import { isArray, isEmpty } from 'lodash'
import { defaultExportMenu, getStepLineWithRangeSeries } from '../helpers'

export default ({ config = {}, data = {}, color = {}, legendLabels = {} }) => {
  const { labelColor } = color
  const {
    series,
    minMaxAxis,
    disabledAnimations = false,
    ...restConfig
  } = config
  const {
    title,
    xKey,
    dateSettings: {
      useDateAxis = false,
      dateFormats = {
        day: 'yyyy-MM-dd',
        week: 'yyyy-MM-dd',
        month: 'yyyy-MM-dd',
        year: 'yyyy-MM-dd'
      },
      baseInterval = {
        timeUnit: 'day',
        count: 1
      }
    } = {},
    scrollbar,
    xAxisRanges,
    yAxisRanges,
    exportingMenu,
    disabledLegend = false
  } = restConfig
  const keys = []

  if (!isEmpty(series)) {
    series.forEach((el) => {
      // get custom legend labels with translated values
      !isEmpty(legendLabels) &&
        Object.keys(legendLabels).forEach((key) => {
          if (el.includes(key)) {
            keys.push(legendLabels[key])
          }
        })
    })
  }

  const xAxisType = useDateAxis ? 'DateAxis' : 'CategoryAxis'
  const dataField = useDateAxis ? 'date' : 'category'

  const chartConfig = {
    data: data,
    type: 'XYChart',
    series: getStepLineWithRangeSeries({
      series,
      otherConfig: {
        legendLabel: keys,
        color,
        disabledAnimations,
        useDateAxis,
        ...restConfig
      }
    }),
    cursor: {},
    xAxes: [
      {
        type: xAxisType,
        dateFormats: dateFormats,
        periodChangeDateFormats: dateFormats,
        skipEmptyPeriods: useDateAxis || undefined,
        baseInterval,
        dataFields: {
          [dataField]: xKey
        },
        axisRanges: getXAxisRanges(xAxisRanges, useDateAxis),
        rangeChangeDuration: 500,
        startLocation: 0.5,
        renderer: {
          labels: {
            rotation: -60,
            verticalCenter: 'left',
            horizontalCenter: 'left',
            fill: labelColor,
            dx: -50
          },
          grid: {
            template: {
              strokeWidth: 0,
              fill: labelColor
            }
          },
          line: {
            strokeOpacity: 0.3,
            fill: labelColor
          },
          minGridDistance: 60,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
          maxLabelPosition: 0.98
        }
      }
    ],
    yAxes: [
      {
        title: {
          text: title || null,
          fill: labelColor
        },
        axisRanges: getYAxisRanges(yAxisRanges, labelColor),
        id: 'CommonYAxis',
        type: 'ValueAxis',
        renderer: {
          line: {
            strokeOpacity: 0.3,
            fill: labelColor
          },
          labels: {
            fill: labelColor
          }
        }
      },
      {
        type: 'ValueAxis',
        renderer: {
          opposite: true,
          line: {
            strokeOpacity: 0
          },
          labels: {
            fill: labelColor
          }
        }
      }
    ],
    scrollbarX: {
      disabled: !scrollbar,
      animationDuration: 500,
      parent: 'bottomAxesContainer'
    },
    legend: {
      disabled: disabledLegend,
      useDefaultMarker: true,
      labels: {
        text: '{name}',
        fill: labelColor
      },
      contentAlign: 'left',
      paddingTop: 10,
      paddingLeft: 45,
      markers: {
        children: [
          {
            width: 20,
            height: 20,
            fontSize: 200,
            strokeWidth: 0,
            paddingTop: 2.5,
            marginLeft: 22.5,
            paddingLeft: 2.5,
            strokeOpacity: 0,
            paddingRight: 2.5,
            paddingBottom: 2.5,
            cornerRadiusTopLeft: 0,
            cornerRadiusTopRight: 0,
            cornerRadiusBottomLeft: 0,
            cornerRadiusBottomRight: 0
          }
        ]
      }
    }
  }

  if (!isEmpty(minMaxAxis)) {
    chartConfig.yAxes = chartConfig.yAxes.map((obj) => ({
      ...obj,
      ...minMaxAxis
    }))
  }

  // Export menu
  if (exportingMenu && exportingMenu.enabled) {
    if (isEmpty(exportingMenu.config)) {
      chartConfig.exporting = defaultExportMenu
    } else {
      chartConfig.exporting = exportingMenu.config
    }
  }
  return chartConfig
}

const getXAxisRanges = (axisRanges, useDateAxis) => {
  if (isEmpty(axisRanges)) return undefined

  const rangeStart = useDateAxis ? 'date' : 'category'
  const rangeEnd = useDateAxis ? 'endDate' : 'endCategory'

  return !isArray(axisRanges)
    ? [
        {
          series: axisRanges.series,
          [rangeStart]: axisRanges.start,
          [rangeEnd]: axisRanges.end,
          contents: {
            fill: axisRanges.color,
            fillOpacity: 0.5
          }
        }
      ]
    : axisRanges?.map(({ series, start, end, color }) => ({
        series,
        [rangeStart]: start,
        [rangeEnd]: end,
        contents: {
          fill: color,
          fillOpacity: 0.5
        }
      }))
}

const getYAxisRanges = (axisRanges, labelColor) => {
  if (isEmpty(axisRanges)) return undefined
  return !isArray(axisRanges)
    ? [
        {
          value: axisRanges?.value,
          grid: {
            stroke: labelColor,
            strokeWidth: 2,
            strokeOpacity: 1
          },
          label: {
            text: axisRanges?.text,
            inside: true,
            fill: labelColor,
            verticalCenter: 'bottom',
            paddingBottom: 3
          }
        }
      ]
    : axisRanges?.map(({ value = '', text = '' }) => ({
        value: value,
        grid: {
          stroke: labelColor,
          strokeWidth: 2,
          strokeOpacity: 1
        },
        label: {
          text: text,
          inside: true,
          fill: labelColor,
          verticalCenter: 'bottom',
          paddingBottom: 3
        }
      }))
}
